import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  Download, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Image as ImageIcon,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import axiosInstance from '../../lib/axios';

export default function BulkUploadDialog({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1: CSV Upload, 2: Results & Image Upload
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/products/csv-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleCsvSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setCsvFile(file);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axiosInstance.post(
        `${API_URL}/api/admin/products/bulk-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setUploadResult(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`Successfully created ${response.data.success_count} products!`);
        setStep(2);
      } else {
        toast.error('No products were created. Check the errors below.');
      }
    } catch (error) {
      console.error('Bulk upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload products');
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
  };

  const handleUploadProductImages = async () => {
    if (!selectedProduct || imageFiles.length === 0) {
      toast.error('Please select a product and images');
      return;
    }

    setUploadingImages(true);
    try {
      const formData = new FormData();
      imageFiles.forEach(file => {
        formData.append('files', file);
      });

      await axiosInstance.post(
        `${API_URL}/api/admin/products/${selectedProduct.id}/upload-images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success(`Images uploaded for ${selectedProduct.name}`);
      
      // Mark this product as having images
      setUploadResult(prev => ({
        ...prev,
        created_products: prev.created_products.map(p => 
          p.id === selectedProduct.id ? { ...p, hasImages: true } : p
        )
      }));
      
      setSelectedProduct(null);
      setImageFiles([]);
      if (imageInputRef.current) imageInputRef.current.value = '';
    } catch (error) {
      console.error('Failed to upload images:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setStep(1);
    setCsvFile(null);
    setUploadResult(null);
    setSelectedProduct(null);
    setImageFiles([]);
    setShowErrors(false);
    onClose();
    if (uploadResult?.success_count > 0) {
      onSuccess?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            Bulk Product Upload
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">How to bulk upload products:</h4>
              <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                <li>Download the CSV template below</li>
                <li>Fill in your product data (keep the header row)</li>
                <li>Upload the filled CSV file</li>
                <li>After upload, add images for each product</li>
              </ol>
            </div>

            {/* Download Template */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h4 className="font-medium text-gray-900 mb-2">Step 1: Download Template</h4>
              <p className="text-sm text-gray-500 mb-4">
                Get the CSV template with all required columns
              </p>
              <Button 
                variant="outline" 
                onClick={handleDownloadTemplate}
                className="gap-2"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4" />
                Download CSV Template
              </Button>
            </div>

            {/* Upload CSV */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h4 className="font-medium text-gray-900 mb-2">Step 2: Upload Your CSV</h4>
              <p className="text-sm text-gray-500 mb-4">
                Upload your filled CSV file with product data
              </p>
              
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvSelect}
                ref={fileInputRef}
                className="hidden"
              />
              
              {csvFile ? (
                <div className="flex items-center justify-center gap-3 mb-4">
                  <FileSpreadsheet className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">{csvFile.name}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      setCsvFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-red-600"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2 mb-4"
                  data-testid="select-csv-btn"
                >
                  <Upload className="w-4 h-4" />
                  Select CSV File
                </Button>
              )}
            </div>

            {/* CSV Format Help */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">CSV Column Reference:</h4>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                <div><code className="bg-gray-200 px-1 rounded">name</code> - Product name (required)</div>
                <div><code className="bg-gray-200 px-1 rounded">description</code> - Description (required)</div>
                <div><code className="bg-gray-200 px-1 rounded">price</code> - Base price (required)</div>
                <div><code className="bg-gray-200 px-1 rounded">collection_name</code> - Collection name (required)</div>
                <div><code className="bg-gray-200 px-1 rounded">sale_price</code> - Sale price (optional)</div>
                <div><code className="bg-gray-200 px-1 rounded">inventory</code> - Stock quantity</div>
                <div><code className="bg-gray-200 px-1 rounded">sku</code> - SKU code</div>
                <div><code className="bg-gray-200 px-1 rounded">tags</code> - Comma-separated tags</div>
                <div><code className="bg-gray-200 px-1 rounded">variants</code> - size:price:qty:weight:sku|...</div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleCsvUpload} 
                disabled={!csvFile || uploading}
                className="btn-primary gap-2"
                data-testid="upload-csv-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload & Process
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && uploadResult && (
          <div className="space-y-6">
            {/* Results Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-700">{uploadResult.success_count}</div>
                <div className="text-sm text-green-600">Products Created</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-red-700">{uploadResult.error_count}</div>
                <div className="text-sm text-red-600">Errors</div>
              </div>
            </div>

            {/* Errors Section */}
            {uploadResult.errors.length > 0 && (
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowErrors(!showErrors)}
                  className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 transition-colors"
                >
                  <span className="flex items-center gap-2 text-red-800 font-medium">
                    <AlertCircle className="w-4 h-4" />
                    View Errors ({uploadResult.errors.length})
                  </span>
                  {showErrors ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showErrors && (
                  <div className="max-h-40 overflow-y-auto p-3 space-y-2">
                    {uploadResult.errors.map((error, idx) => (
                      <div key={idx} className="text-sm text-red-700 bg-red-50 p-2 rounded">
                        <strong>Row {error.row}:</strong> {error.error}
                        {error.product && <span className="text-gray-600"> ({error.product})</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Image Upload Section */}
            {uploadResult.created_products.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-blue-600" />
                  Add Images to Products
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Select a product and upload images for it
                </p>

                {/* Product Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Product:
                  </label>
                  <select
                    value={selectedProduct?.id || ''}
                    onChange={(e) => {
                      const product = uploadResult.created_products.find(p => p.id === e.target.value);
                      setSelectedProduct(product || null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    data-testid="select-product-for-images"
                  >
                    <option value="">-- Select a product --</option>
                    {uploadResult.created_products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} {product.hasImages ? '✓' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Image Selection */}
                {selectedProduct && (
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      ref={imageInputRef}
                      className="hidden"
                    />
                    
                    <Button 
                      variant="outline" 
                      onClick={() => imageInputRef.current?.click()}
                      className="w-full gap-2"
                    >
                      <ImageIcon className="w-4 h-4" />
                      Select Images for "{selectedProduct.name}"
                    </Button>

                    {imageFiles.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-sm text-gray-600 mb-2">
                          {imageFiles.length} image(s) selected
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {imageFiles.map((file, idx) => (
                            <div key={idx} className="text-xs bg-white px-2 py-1 rounded border">
                              {file.name}
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={handleUploadProductImages}
                          disabled={uploadingImages}
                          className="mt-3 btn-primary gap-2"
                          data-testid="upload-product-images-btn"
                        >
                          {uploadingImages ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Upload Images
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button onClick={handleClose} className="btn-primary">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
